import { AutoComplete, Dropdown, Icon, Input } from '@ics-portal/guardian';
import { clsx } from 'clsx';
import { type ReactNode, useEffect, useState } from 'react';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import { type StringOption } from '@models/Option';
import { useNewComponentRequestOptions } from '@hooks/NewComponentRequests/useNewComponentRequestOptions';
import { type ComponentCreateRequestRequestPriority } from '@gql/graphql';
import s from './NewComponentRequestForm.module.scss';

export interface NewComponentRequestFormValue {
    id?: string;
    componentVendor: string | StringOption;
    componentName: string;
    componentVersion: string;
    requestPriority?: ComponentCreateRequestRequestPriority;
    referenceWeb?: string;
    referenceSecurity?: string;
}

interface NewComponentRequestFormProps {
    form: UseFormReturn<NewComponentRequestFormValue>;
    header?: string;
    compactVersion?: boolean;
}

export function NewComponentRequestForm({
    form,
    header,
    compactVersion,
}: NewComponentRequestFormProps): ReactNode {
    const {
        vendors,
        fetchVendors,
        priorities,
        componentNames,
        componentVersions,
        fetchComponents,
    } = useNewComponentRequestOptions();

    const [filteredVersions, setFilteredVersions] = useState<string[]>([]);

    const componentVersion = form.watch('componentVersion');
    const componentName = form.watch('componentName');
    const componentVendor = form.watch('componentVendor');

    useEffect(() => {
        if (typeof componentVendor === 'string') {
            fetchVendors(componentVendor);
        } else if (componentVendor && Object.hasOwn(componentVendor, 'label')) {
            fetchVendors(componentVendor.label);
        }

        if (componentName) {
            fetchComponents(componentName);
        }
    }, []);

    useEffect(() => {
        filterVersions(componentVersion);
    }, [componentVersions]);

    useEffect(() => {
        if (componentName) {
            fetchComponents(componentName, componentVendor);
        }
    }, [componentVendor]);

    function filterVersions(query: string): void {
        if (!query) {
            return;
        }

        const filtered = componentVersions.filter((version) =>
            version.toLowerCase().includes(query.toLowerCase()),
        );
        setFilteredVersions(filtered);
    }

    const wrapperClassName = clsx(
        'flex flex-column gap-2',
        compactVersion ? s.newComponentRequestFormCompact : '',
    );

    return (
        <FormProvider {...form}>
            <div className={wrapperClassName}>
                {header ? <h3>{header}</h3> : null}
                <AutoComplete
                    name="componentVendor"
                    field="label"
                    label="Vendor"
                    placeholder="Type 3 characters to start searching"
                    completeMethod={(e) => {
                        fetchVendors(e.query);
                    }}
                    prefixIcon={<Icon name="search" />}
                    suggestions={vendors}
                    minLength={3}
                    required
                />
                <AutoComplete
                    name="componentName"
                    label="Name"
                    placeholder="Enter name"
                    completeMethod={(e) => {
                        fetchComponents(e.query, componentVendor);
                    }}
                    prefixIcon={<Icon name="search" />}
                    suggestions={componentNames}
                    required
                />
                <AutoComplete
                    name="componentVersion"
                    label="Version"
                    placeholder="Enter version"
                    completeMethod={(e) => {
                        filterVersions(e.query);
                    }}
                    prefixIcon={<Icon name="search" />}
                    suggestions={filteredVersions}
                    required
                />
                <Dropdown
                    name="requestPriority"
                    label="Priority"
                    placeholder="Select priority"
                    options={priorities}
                    required
                />
                <Input
                    name="referenceWeb"
                    label="Website URL"
                    placeholder="Enter website URL"
                />
                <Input
                    name="referenceSecurity"
                    label="Security URL"
                    placeholder="Enter security URL"
                />
            </div>
        </FormProvider>
    );
}
